'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { locales, type Locale } from '@/i18n/config'
import { localizePathname } from '@/i18n/path'
import { cn } from '@/lib/utils'

type Props = {
  locale: Locale
  ariaLabel: string
  /** `dark` = over the hero image, `light` = over a white bar. */
  tone?: 'light' | 'dark'
}

const SHORT: Record<Locale, string> = { en: 'EN', rs: 'SR' }

/** Inline "EN | SR" switch, matching the mock's header. */
export function LocaleToggle({ locale, ariaLabel, tone = 'dark' }: Props) {
  const pathname = usePathname() ?? `/${locale}`
  const dark = tone === 'dark'

  return (
    <div data-component-id="src/components/home/LocaleToggle.tsx:25:4" data-component-path="src/components/home/LocaleToggle.tsx" data-component-line="25" data-component-end-line="59" data-component-file="LocaleToggle.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%20text-%5B0.8125rem%5D%20font-semibold%20tracking-wide%22%7D"
      aria-label={ariaLabel}
      className="flex items-center gap-1.5 text-[0.8125rem] font-semibold tracking-wide"
    >
      {locales.map((lng, index) => {
        const active = lng === locale
        return (
          <span data-component-id="src/components/home/LocaleToggle.tsx:32:10" data-component-path="src/components/home/LocaleToggle.tsx" data-component-line="32" data-component-end-line="56" data-component-file="LocaleToggle.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%22%7D" key={lng} className="flex items-center gap-1.5">
            {index > 0 ? (
              <span data-component-id="src/components/home/LocaleToggle.tsx:34:14" data-component-path="src/components/home/LocaleToggle.tsx" data-component-line="34" data-component-end-line="36" data-component-file="LocaleToggle.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%7C%22%7D" className={dark ? 'text-white/30' : 'text-gwt-line'} aria-hidden>
                |
              </span>
            ) : null}
            <Link data-component-id="src/components/home/LocaleToggle.tsx:38:12" data-component-path="src/components/home/LocaleToggle.tsx" data-component-line="38" data-component-end-line="55" data-component-file="LocaleToggle.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
              href={localizePathname(pathname, lng)}
              prefetch={false}
              hrefLang={lng === 'rs' ? 'sr' : lng}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'transition-colors',
                active
                  ? dark
                    ? 'text-white'
                    : 'text-gwt-ink'
                  : dark
                    ? 'text-white/55 hover:text-white/85'
                    : 'text-gwt-muted hover:text-gwt-ink',
              )}
            >
              {SHORT[lng]}
            </Link>
          </span>
        )
      })}
    </div>
  )
}
